import React, { useCallback, useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { OrdemProducaoService } from "../service/ordem_producao_service";

const num = (valor) => {
	const n = parseFloat(valor);
	return Number.isFinite(n) ? n : 0;
};

const formatarNumero = (valor) => num(valor).toLocaleString("pt-BR");

const formatarData = (valor) => {
	if (!valor) return "-";
	const data = new Date(valor);
	if (Number.isNaN(data.getTime())) return "-";
	// As datas chegam como @db.Date (meia-noite UTC): sem timeZone: "UTC" o
	// fuso local jogaria a exibicao para o dia anterior.
	return data.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

const nomeProduto = (produto) => {
	const cultura = produto?.cultivo?.cultura;
	if (!cultura) return "-";
	const variedade = produto.cultivo.variedade ? ` - ${produto.cultivo.variedade}` : "";
	return `${cultura}${variedade}`;
};

// Produto consumido por uma etapa: vem sempre de uma OP anterior, entao a
// identificacao e o produto final dela mais o lote de rastreabilidade.
const descricaoProdutoEtapa = (etapa) => {
	const origem = etapa?.ordemProducaoProduto;
	if (!origem) return "-";
	const produto = nomeProduto(origem.produtoFinal);
	const lote = origem.loteRastreabilidade ? ` | Lote ${origem.loteRastreabilidade}` : "";
	return `OP ${origem.numeroOP}${produto !== "-" ? ` - ${produto}` : ""}${lote}`;
};

const descricaoEmbalagem = (embalagem) => {
	if (!embalagem?.descricao) return "-";
	const unidade = embalagem.unidadeMedida?.sigla || "";
	return unidade ? `${embalagem.descricao} (${embalagem.quantidade} ${unidade})` : embalagem.descricao;
};

function Campo({ label, valor, tamanho = { xs: 12, sm: 6, md: 3 } }) {
	return (
		<Grid size={tamanho}>
			<Typography variant="caption" sx={{ color: "#999", display: "block" }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ color: "#333", fontWeight: 500 }}>
				{valor ?? "-"}
			</Typography>
		</Grid>
	);
}

/**
 * Consulta somente-leitura de uma ordem de produção: dados gerais, rendimento e
 * as etapas do processo com entrada/saída/perdas.
 */
function ModalVisualizarOP({ open, onClose, ordem }) {
	const [loading, setLoading] = useState(false);
	const [erro, setErro] = useState("");
	const [detalhe, setDetalhe] = useState(null);

	const ordemId = ordem?.id;

	const carregar = useCallback(async () => {
		setLoading(true);
		setErro("");
		try {
			const ordemProducaoService = new OrdemProducaoService();
			const resultado = await ordemProducaoService.getById(ordemId);
			setDetalhe(resultado || null);
		} catch (error) {
			console.error("Erro ao carregar ordem de produção:", error);
			setErro(error.message || "Erro ao carregar ordem de produção");
		} finally {
			setLoading(false);
		}
	}, [ordemId]);

	useEffect(() => {
		if (open && ordemId) {
			carregar();
			return;
		}

		setDetalhe(null);
		setErro("");
	}, [open, ordemId, carregar]);

	// Enquanto o detalhe nao chega, os dados da linha da listagem ja preenchem
	// o cabecalho — o modal abre com conteudo em vez de uma area vazia.
	const dados = detalhe || ordem || {};
	const etapas = Array.isArray(dados.etapas) ? dados.etapas : [];
	// Colunas de produto so aparecem quando alguma etapa consome produto.
	const temEtapaComProduto = etapas.some((etapa) => etapa.solicitaProduto);

	const quantidadeEntrada = num(dados.quantidadeEntrada);
	const quantidadeFinal = etapas.length
		? num(etapas[etapas.length - 1].quantidadeSaida)
		: num(dados.quantidadeFinal);
	const rendimento = quantidadeEntrada > 0 ? ((quantidadeFinal / quantidadeEntrada) * 100).toFixed(2) : "0.00";
	const totalPerdas = etapas.reduce((soma, etapa) => soma + num(etapa.perdas), 0);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ fontSize: "18px", fontWeight: 600, color: "#1a9e8f" }}>
				Ordem de Produção {dados.numeroOP ? `nº ${dados.numeroOP}` : ""}
			</DialogTitle>
			<Divider />
			<DialogContent>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress sx={{ color: "#1a9e8f" }} />
					</Box>
				) : (
					<>
						{erro && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{erro}
							</Alert>
						)}

						<Typography variant="subtitle2" sx={{ color: "#1a9e8f", fontWeight: 600, mb: 1.5 }}>
							Dados da Ordem
						</Typography>
						<Grid container spacing={2} sx={{ mb: 3 }}>
							<Campo label="Data" valor={formatarData(dados.data)} />
							<Campo label="Produto Entrada" valor={nomeProduto(dados.produtoEntrada)} />
							<Campo
								label="Lote Rastreabilidade"
								valor={dados.loteRastreabilidade || dados.loteConsolidado?.codigoRastreio || "-"}
							/>
							<Campo label="Processo" valor={dados.processo?.nome || "-"} />
							<Campo label="Produto Final" valor={nomeProduto(dados.produtoFinal)} />
							<Campo label="Embalagem" valor={descricaoEmbalagem(dados.embalagemProdutoFinal)} />
							<Campo label="Quant. Entrada" valor={formatarNumero(quantidadeEntrada)} />
							<Campo label="Quant. Final" valor={formatarNumero(quantidadeFinal)} />
							<Campo label="Volumes" valor={formatarNumero(dados.volumes)} />
							<Campo label="Perdas Totais" valor={formatarNumero(totalPerdas)} />
							<Campo label="Rendimento" valor={`${rendimento}%`} />
						</Grid>

						<Typography variant="subtitle2" sx={{ color: "#1a9e8f", fontWeight: 600, mb: 1.5 }}>
							Etapas do Processo
						</Typography>
						{etapas.length === 0 ? (
							<Typography variant="body2" sx={{ color: "#999" }}>
								Nenhuma etapa registrada para esta ordem.
							</Typography>
						) : (
							<TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}>
								<Table size="small">
									<TableHead>
										<TableRow sx={{ bgcolor: "#f5f5f5" }}>
											<TableCell sx={{ fontWeight: 600 }}>Etapa</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>Quant. Entrada</TableCell>
											{/* Produto utilizado entra no total da etapa: vem antes da saída */}
											{temEtapaComProduto && (
												<>
													<TableCell sx={{ fontWeight: 600 }}>Produto Utilizado</TableCell>
													<TableCell sx={{ fontWeight: 600 }}>Quant. Utilizada</TableCell>
												</>
											)}
											<TableCell sx={{ fontWeight: 600 }}>Quant. Saída</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>Perdas</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{etapas.map((etapa, index) => (
											<TableRow key={etapa.id || index}>
												<TableCell>{etapa.numeroEtapa}</TableCell>
												<TableCell>{etapa.nomeEtapa}</TableCell>
												<TableCell>{formatarNumero(etapa.quantidadeEntrada)}</TableCell>
												{temEtapaComProduto && (
													<>
														<TableCell>
															{etapa.solicitaProduto ? descricaoProdutoEtapa(etapa) : "-"}
														</TableCell>
														<TableCell>
															{etapa.solicitaProduto ? formatarNumero(etapa.quantidadeProduto) : "-"}
														</TableCell>
													</>
												)}
												<TableCell>{formatarNumero(etapa.quantidadeSaida)}</TableCell>
												<TableCell
													sx={{
														color: num(etapa.perdas) > 0 ? "#ff4d4d" : "#666",
														fontWeight: num(etapa.perdas) > 0 ? 600 : 400,
													}}
												>
													{formatarNumero(etapa.perdas)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</>
				)}
			</DialogContent>
			<Divider />
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} sx={{ color: "#666", textTransform: "none" }}>
					Fechar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default ModalVisualizarOP;
